import React from 'react';
import DOMPurify from 'dompurify';

/**
 * SafeHTML component to render HTML content safely
 * Uses DOMPurify to sanitize HTML and prevent XSS attacks
 */
export const SafeHTML = ({ html, className = '' }) => {
  if (!html) return null;
  
  // Check if content looks like HTML
  const isHTML = /<[a-z][\s\S]*>/i.test(html);
  
  if (!isHTML) {
    // If it's plain text, preserve whitespace and line breaks
    return (
      <div className={`whitespace-pre-wrap ${className}`}>
        {html}
      </div>
    );
  }
  
  // Sanitize HTML to prevent XSS
  const cleanHTML = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'strike', 's',
      'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'blockquote', 'pre', 'code',
      'hr', 'span', 'div', 'mark'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'target', 'rel',
      'class', 'style', 'id',
      'colspan', 'rowspan',
      'width',
    ],
    ALLOW_DATA_ATTR: false,
  });
  
  return (
    <>
      <div 
        className={`safe-html-content ${className}`}
        dangerouslySetInnerHTML={{ __html: cleanHTML }}
      />
      <style>{`
        .safe-html-content {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .safe-html-content p {
          margin-bottom: 1em;
          min-height: 1.5em;
        }
        .safe-html-content p:last-child {
          margin-bottom: 0;
        }
        .safe-html-content br {
          display: block;
          content: "";
          margin-top: 0.5em;
        }
        .safe-html-content h1 { font-size: 2rem; font-weight: bold; margin: 1rem 0; }
        .safe-html-content h2 { font-size: 1.5rem; font-weight: bold; margin: 0.875rem 0; }
        .safe-html-content h3 { font-size: 1.25rem; font-weight: bold; margin: 0.75rem 0; }
        .safe-html-content h4 { font-size: 1.125rem; font-weight: bold; margin: 0.625rem 0; }
        .safe-html-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .safe-html-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .safe-html-content ul li, .safe-html-content ol li { margin: 0.25rem 0; }
        .safe-html-content blockquote {
          border-left: 3px solid #e5e7eb;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #6b7280;
        }
        .safe-html-content pre {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .safe-html-content code {
          background-color: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.9em;
        }
        .safe-html-content pre code {
          background: none;
          padding: 0;
        }
        .safe-html-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .safe-html-content a:hover {
          color: #2563eb;
        }
        .safe-html-content img {
          /* max-width here is a fallback; images inserted via the editor already
             carry max-width:100% in their inline style for guaranteed responsiveness */
          max-width: 100% !important;
          height: auto;
          border-radius: 0.5rem;
          margin: 0.5rem 0;
        }
        .safe-html-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .safe-html-content table th,
        .safe-html-content table td {
          border: 1px solid #e5e7eb;
          padding: 0.5rem;
          text-align: left;
        }
        .safe-html-content table th {
          background-color: #f3f4f6;
          font-weight: bold;
        }
        .safe-html-content hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 1.5rem 0;
        }
        .safe-html-content strong, .safe-html-content b {
          font-weight: bold;
        }
        .safe-html-content em, .safe-html-content i {
          font-style: italic;
        }
        .safe-html-content u {
          text-decoration: underline;
        }
        .safe-html-content s, .safe-html-content strike {
          text-decoration: line-through;
        }
        .safe-html-content mark {
          background-color: #fef08a;
          padding: 0.1rem 0.2rem;
        }
      `}</style>
    </>
  );
};

export default SafeHTML;
